import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { MapPin, Search, Trash2, Plus, ExternalLink, Calendar } from "lucide-react";
import { toast } from "sonner";

export default function VenueDiscovery() {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [filter, setFilter] = useState("all"); // all | CONFIRMED | POSSIBLE
  const [countryFilter, setCountryFilter] = useState("all");

  useEffect(() => {
    loadVenues();
  }, []);

  const loadVenues = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/venues/list");
      setVenues(res.data.venues || []);
    } catch (err) {
      toast.error("Failed to load venues");
    } finally {
      setLoading(false);
    }
  };

  const runDiscovery = async () => {
    setDiscovering(true);
    try {
      const res = await api.post("/api/venues/discover", {
        countries: ["Germany", "Spain", "Netherlands", "Belgium", "France"],
        venue_types: ["gay sauna", "sex club", "gay bar"],
        max_results_per_query: 10,
      });
      toast.success(`✅ Discovered ${res.data.discovered} venues (${res.data.new_venues} new)`);
      loadVenues();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Discovery failed. Check if SERPAPI_KEY is configured.");
    } finally {
      setDiscovering(false);
    }
  };

  const addToCRM = async (venue) => {
    try {
      // Create CRM entry from venue
      await api.post("/api/crm/", {
        stage_name: venue.name,
        category: "venue",
        city: venue.city,
        country: venue.country,
        photos: venue.image_url ? [venue.image_url] : [],
        website: venue.website,
        notes: `${venue.venue_type} · ${venue.live_show_status}\n${venue.event_evidence || ""}\n${venue.description || ""}`,
      });
      toast.success(`✅ ${venue.name} added to CRM`);
      
      // Remove from discovery list
      await api.delete(`/api/venues/${venue.venue_id}`);
      loadVenues();
    } catch (err) {
      toast.error("Failed to add to CRM");
    }
  };

  const deleteVenue = async (venueId) => {
    if (!confirm("Remove this venue?")) return;
    try {
      await api.delete(`/api/venues/${venueId}`);
      toast.success("Venue removed");
      loadVenues();
    } catch (err) {
      toast.error("Failed to delete venue");
    }
  };

  const filteredVenues = venues.filter((v) => {
    if (filter !== "all" && v.live_show_status !== filter) return false;
    if (countryFilter !== "all" && v.country !== countryFilter) return false;
    return true;
  });

  const countries = [...new Set(venues.map(v => v.country))];
  const confirmedCount = venues.filter(v => v.live_show_status === "CONFIRMED").length;
  const possibleCount = venues.filter(v => v.live_show_status === "POSSIBLE").length;

  return (
    <div className="min-h-screen" style={{ background: "var(--gio-bg)" }}>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              🏢 Venue Discovery
            </h1>
            <p className="text-zinc-400">Discover European venues hosting live sex shows & porn star events</p>
          </div>
          <button
            onClick={runDiscovery}
            disabled={discovering}
            className="px-6 py-3 rounded-lg font-semibold transition flex items-center gap-2"
            style={{
              background: discovering ? "#71717a" : "var(--gio-primary)",
              color: "#000",
            }}>
            {discovering ? (
              <>
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                Scanning Europe...
              </>
            ) : (
              <>
                <Search size={18} />
                Run Discovery
              </>
            )}
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="flex gap-2">
            {[
              { v: "all", l: "All Venues" },
              { v: "CONFIRMED", l: "✅ Confirmed" },
              { v: "POSSIBLE", l: "🟡 Possible" },
            ].map(({ v, l }) => (
              <button
                key={v}
                onClick={() => setFilter(v)}
                className="px-4 py-2 rounded-lg font-semibold transition"
                style={{
                  background: filter === v ? "var(--gio-primary)" : "var(--gio-surface)",
                  color: filter === v ? "#000" : "#fff",
                  border: `1px solid ${filter === v ? "var(--gio-primary)" : "var(--gio-border)"}`,
                }}>
                {l}
              </button>
            ))}
          </div>

          <select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="px-4 py-2 rounded-lg font-semibold"
            style={{ background: "var(--gio-surface)", color: "#fff", border: "1px solid var(--gio-border)" }}>
            <option value="all">All Countries</option>
            {countries.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="p-4 rounded-xl" style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}>
            <div className="text-2xl font-bold text-white">{venues.length}</div>
            <div className="text-sm text-zinc-400">Total Venues</div>
          </div>
          <div className="p-4 rounded-xl" style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)" }}>
            <div className="text-2xl font-bold text-green-400">{confirmedCount}</div>
            <div className="text-sm text-zinc-400">Confirmed Events</div>
          </div>
          <div className="p-4 rounded-xl" style={{ background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.3)" }}>
            <div className="text-2xl font-bold text-yellow-400">{possibleCount}</div>
            <div className="text-sm text-zinc-400">Possible Events</div>
          </div>
          <div className="p-4 rounded-xl" style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}>
            <div className="text-2xl font-bold text-white">{countries.length}</div>
            <div className="text-sm text-zinc-400">Countries</div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <div className="text-zinc-400">Loading venues...</div>
          </div>
        )}

        {/* Empty State */}
        {!loading && venues.length === 0 && (
          <div className="text-center py-12 rounded-xl" style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}>
            <MapPin size={48} className="mx-auto mb-4 text-zinc-600" />
            <h3 className="text-xl font-bold text-white mb-2">No venues discovered yet</h3>
            <p className="text-zinc-400 mb-4">Run discovery to find European venues with live shows</p>
            <button
              onClick={runDiscovery}
              className="px-6 py-3 rounded-lg font-semibold transition"
              style={{ background: "var(--gio-primary)", color: "#000" }}>
              <Search size={18} className="inline mr-2" />
              Start Discovery
            </button>
          </div>
        )}

        {/* Venue Grid */}
        {!loading && filteredVenues.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredVenues.map((venue) => (
              <VenueCard
                key={venue.venue_id}
                venue={venue}
                onAddToCRM={addToCRM}
                onDelete={deleteVenue}
              />
            ))}
          </div>
        )}

        {!loading && venues.length > 0 && filteredVenues.length === 0 && (
          <div className="text-center py-12 rounded-xl" style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}>
            <p className="text-zinc-400">No venues match your filters</p>
          </div>
        )}
      </div>
    </div>
  );
}

function VenueCard({ venue, onAddToCRM, onDelete }) {
  const statusColors = {
    CONFIRMED: { bg: "rgba(34,197,94,0.2)", color: "#22c55e", border: "rgba(34,197,94,0.3)" },
    POSSIBLE: { bg: "rgba(251,191,36,0.2)", color: "#fbbf24", border: "rgba(251,191,36,0.3)" },
  };

  const colors = statusColors[venue.live_show_status] || statusColors.POSSIBLE;

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        background: "var(--gio-surface)",
        border: `1px solid ${colors.border}`,
      }}>
      {/* Image */}
      {venue.image_url ? (
        <img
          src={venue.image_url}
          alt={venue.name}
          className="w-full h-40 object-cover"
        />
      ) : (
        <div
          className="w-full h-40 flex items-center justify-center"
          style={{ background: "var(--gio-surface-2)" }}>
          <MapPin size={48} className="text-zinc-600" />
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {/* Status Badge */}
        <div className="flex items-center justify-between mb-2">
          <span
            className="text-xs px-2 py-1 rounded font-bold"
            style={{ background: colors.bg, color: colors.color, border: `1px solid ${colors.border}` }}>
            {venue.live_show_status === "CONFIRMED" ? "✅ CONFIRMED" : "🟡 POSSIBLE"}
          </span>
          <span className="text-xs px-2 py-1 rounded" style={{ background: "var(--gio-primary-soft)", color: "var(--gio-primary)" }}>
            {venue.venue_type}
          </span>
        </div>

        {/* Name */}
        <h3 className="text-lg font-bold text-white mb-1">{venue.name}</h3>

        {/* Location */}
        <div className="flex items-center gap-1 text-sm text-zinc-400 mb-2">
          <MapPin size={14} />
          {venue.city}, {venue.country}
        </div>

        {/* Event Evidence */}
        {venue.event_evidence && (
          <div
            className="text-xs p-2 rounded mb-3"
            style={{ background: colors.bg, color: colors.color, border: `1px solid ${colors.border}` }}>
            {venue.event_evidence}
          </div>
        )}

        {/* Description */}
        {venue.description && (
          <p className="text-sm text-zinc-400 mb-3 line-clamp-2">{venue.description}</p>
        )}

        {/* Website */}
        {venue.website && (
          <a
            href={venue.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 mb-3">
            <ExternalLink size={12} />
            Visit website
          </a>
        )}

        {/* Discovered Date */}
        <div className="text-xs text-zinc-500 flex items-center gap-1 mb-3">
          <Calendar size={12} />
          Discovered {new Date(venue.discovered_at).toLocaleDateString()}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => onAddToCRM(venue)}
            className="flex-1 px-3 py-2 rounded text-sm font-semibold transition flex items-center justify-center gap-1"
            style={{ background: "#22c55e", color: "#000" }}>
            <Plus size={14} />
            Add to CRM
          </button>
          <button
            onClick={() => onDelete(venue.venue_id)}
            className="p-2 rounded text-red-400 hover:text-red-300 transition"
            style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)" }}>
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
